import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import BookingCalendar from "../components/BookingCalendar";
import EmptyServiceCard from "../components/EmptyServiceCard";
import DepositPaymentModal from "../components/DepositPaymentModal";
import WaitingListModal from "../components/WaitingListModal";
import AntiSpamCaptcha from "../components/AntiSpamCaptcha";
import { useBooking } from "../contexts/BookingContext";
import useScrollSuave from '../hooks/useScrollSuave';
import "./BusinessPage.css";
import LoadingSpinner from "../components/LoadingSpinner";
import { useDemo } from '../contexts/DemoContext';
import { aplicarBranding, limpiarBranding, COLORES_POR_DEFECTO } from '../lib/branding';
import HeroNegocio from '../components/business/HeroNegocio';
import SelectorOpciones from '../components/business/SelectorOpciones';
import PasoSeccion from '../components/business/PasoSeccion';
import PasoDeReserva from '../components/business/PasoDeReserva';
import { obrasSocialesDelNegocio, obrasSociales as TODAS_LAS_OBRAS } from '../data/obrasSociales';
import ResumenReserva from '../components/business/ResumenReserva';
import MensajeNegocio from '../components/business/MensajeNegocio';
import SEO from '../components/SEO';
import { businessTemplates } from '../data/rubros';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { MapPin, LayoutDashboard, Copy, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { buscarServicios } from '../lib/servicios';
import { aFechaISO } from '../lib/calendario';
import BotonesDeCalendario from '../components/business/BotonesDeCalendario';
import { precioDelTurno } from '../lib/precios';
import { rolesDe } from '../lib/roles';
// gola
// ===============================================
// INICIO: Subpaso 1.2 - Conectar página pública a la API
// ===============================================

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001"; // CORRECCIÓN: Apuntar al puerto correcto del backend

/**
 * A partir de cuántos servicios se ofrece el buscador.
 *
 * Es el único número que decide si el campo aparece o no, así que cambiar la
 * idea es cambiar esto y nada más.
 *
 * No se muestra siempre a propósito: con cuatro o cinco tarjetas a la vista, un
 * campo de búsqueda arriba es un paso de más antes del único que importa, y
 * encima sugiere que hay algo escondido cuando está todo visible. Recién cuando
 * la lista pasa a ser algo que hay que scrolear —una clínica con treinta
 * prácticas, no una peluquería con seis cortes— buscar es más rápido que mirar.
 */
const MINIMO_PARA_BUSCAR = 8;

/**
 * Horario del negocio de demostración, y también el de cada profesional.
 * Formato { enabled, ranges: [{ start, end }] }, el mismo que guarda el
 * onboarding y el que lee BookingCalendar.
 */
const horarioDemo = {
  monday: { enabled: true, ranges: [{ start: '09:00', end: '20:00' }] },
  tuesday: { enabled: true, ranges: [{ start: '09:00', end: '20:00' }] },
  wednesday: { enabled: true, ranges: [{ start: '09:00', end: '20:00' }] },
  thursday: { enabled: true, ranges: [{ start: '09:00', end: '20:00' }] },
  friday: { enabled: true, ranges: [{ start: '09:00', end: '20:00' }] },
  saturday: { enabled: true, ranges: [{ start: '10:00', end: '18:00' }] },
  sunday: { enabled: false, ranges: [] },
};

/**
 * Página pública del negocio.
 *
 * Tres modos, y todos usan estos mismos componentes:
 * - normal: resuelve el negocio por el slug de la URL y lo trae de la API.
 * - `isDemo`: datos de demostración en memoria, la reserva va a DemoContext.
 * - `datosPreview`: datos que todavía NO se guardaron, para la vista previa del
 *   onboarding. Ver el bloque de abajo.
 *
 * Forma de `datosPreview` (lo que arma el onboarding):
 *   {
 *     businessInfo: { name, slug, address, phone, email, mensaje },
 *     services: [], staff: [], resources: [],
 *     schedule: { monday: { enabled, ranges: [{ start, end }] }, ... },
 *     branding: { primaryColor, secondaryColor, accentColor },
 *     logo: 'https://…'   // opcional, la imagen del negocio
 *   }
 *
 * En ese modo la página es de solo mirar: no hace ni un fetch, no toca
 * localStorage, y no se puede reservar ni gestionar un turno (no hay negocio
 * todavía contra el que hacerlo). El branding se aplica igual, que es el punto
 * de la vista previa, y se limpia al desmontar como en el modo normal.
 */
export default function BusinessPage({ isDemo = false, datosPreview = null }) {

  // Inercia al scrollear, igual que en la landing.
  //
  // `datosPreview` la apaga: en ese modo este componente se monta DENTRO del
  // onboarding, como vista previa, y ahí quien scrollea es la tarjeta del paso.
  // Una librería que toma el control del scroll de la ventana dejaría esa
  // tarjeta trabada.
  // La instancia hace falta para llevar la pantalla al paso siguiente: mover el
  // scroll con `window.scrollTo` mientras la inercia corre es pelearle, y el
  // resultado tiembla.
  const scrollSuave = useScrollSuave({ activo: !datosPreview });
  const esPreview = !!datosPreview;
  // En vista previa no hay URL de la que sacar el slug: el negocio ni existe.
  const params = useParams();
  const slug = esPreview ? null : params.slug;
  // Sueltos, para poder usarlos como dependencias estables (ver el efecto del
  // branding más abajo).
  const previewPrimario = datosPreview?.branding?.primaryColor;
  const previewSecundario = datosPreview?.branding?.secondaryColor;
  const previewAcento = datosPreview?.branding?.accentColor;
  const location = useLocation();
  const navigate = useNavigate();
  const { addReserva, agenda } = useBooking();
  const { addDemoBooking } = useDemo();
  const [showEmptyCard, setShowEmptyCard] = useState(false);
  /**
   * Lo que la persona escribió para buscar un servicio.
   *
   * Filtra qué tarjetas se ven, y nada más: el servicio ya elegido no se
   * deselecciona por escribir en el buscador, así que cambiar de idea sobre qué
   * buscar no borra los pasos que ya se completaron abajo.
   */
  const [busquedaDeServicio, setBusquedaDeServicio] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [bookingData, setBookingData] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [managementCode, setManagementCode] = useState("");
  const [managementError, setManagementError] = useState("");
  const [foundBooking, setFoundBooking] = useState(null);
  const [managementStep, setManagementStep] = useState("search"); // "search", "options"
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);


  // 🔄 NUEVOS ESTADOS PARA REPROGRAMACIÓN
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null); // Turno actual que se está reprogramando
  
  // 💰 NUEVOS ESTADOS PARA SEÑAS/DEPÓSITOS
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  
  // 💸 ESTADO PARA PRECIO DINÁMICO
  const [dynamicPriceMultiplier, setDynamicPriceMultiplier] = useState(1);
  
  // 📋 ESTADO PARA LISTA DE ESPERA
  const [showWaitingListModal, setShowWaitingListModal] = useState(false);
  
  // 🛡️ ESTADOS PARA SISTEMA ANTI-SPAM
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [pendingBookingForSpam, setPendingBookingForSpam] = useState(null);
  const [isCheckingSpam, setIsCheckingSpam] = useState(false);
  const [spamCheckResult, setSpamCheckResult] = useState(null);
  
  // Estados para los datos del negocio, carga y errores
  const [businessData, setBusinessData] = useState(null);

  /* DIAGNÓSTICO — [DIAG-1] Qué se eligió y qué recibe el calendario.
     Va DEBAJO de `businessData` a propósito: la lista de dependencias se evalúa
     durante el render, así que arriba rompía por zona muerta temporal.
     Temporal: sacar junto con [DIAG-2..4] de BookingCalendar.jsx. */
  useEffect(() => {
    console.log('[DIAG-1] selección →', {
      negocioId: businessData?.id ?? '(SIN ID)',
      servicio: selectedService?.name ?? null,
      servicioDuracion: selectedService?.duration ?? null,
      staffSelectionMode: selectedService?.staffSelectionMode ?? '(no definido)',
      profesionalId: selectedStaff?.id ?? null,
      profesionalNombre: selectedStaff?.name ?? null,
      esSinPreferencia: selectedStaff?.isAnyStaff ?? false,
      lugarId: selectedResource?.id ?? null,
    });
  }, [businessData, selectedService, selectedStaff, selectedResource]);
  const [loading, setLoading] = useState(true);
  // El branding llega por fetch, o sea después del primer pintado. Sin esto la
  // página se pinta con el naranja de Aturno y salta al color del negocio.
  const [brandingListo, setBrandingListo] = useState(false);
  const [error, setError] = useState(null);
  const [confirmationClosing, setConfirmationClosing] = useState(false);

  // 🔄 NUEVA FUNCIÓN: Buscar reserva para reprogramación
  const fetchBookingForReschedule = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/by-code/${encodeURIComponent(code.trim())}?businessId=${businessData?.id || ''}`);
      const data = await response.json();

      if (response.ok) {
        setCurrentBooking(data.booking);
        
        // Auto-seleccionar servicio, staff y resource del turno actual
        if (businessData?.services) {
          const service = businessData.services.find(s => s.name === data.booking.service.name);
          if (service) {
            setSelectedService(service);
            setSelectedStaff(data.booking.staff);
            setSelectedResource(data.booking.resource);
          }
        }
      } else {
        console.error('Error al cargar reserva para reprogramar:', data.error);
        setError("No se pudo cargar la reserva para reprogramar");
      }
    } catch (error) {
      console.error("Error de conexión al cargar reserva:", error);
      setError("Error de conexión");
    }
  };

  // 🔄 useEffect para detectar reprogramación desde URL params
  useEffect(() => {
    // La vista previa vive dentro del onboarding: la query string de esa URL no
    // habla de turnos de este negocio, y no hay a quién preguntarle.
    if (esPreview) return;

    const urlParams = new URLSearchParams(location.search);
    const rescheduleCode = urlParams.get('reschedule');

    if (rescheduleCode) {
      setIsRescheduling(true);
      // Buscar la reserva automáticamente cuando tengamos businessData
      if (businessData) {
        fetchBookingForReschedule(rescheduleCode);
      }
    }
  }, [location.search, businessData, esPreview]);

  /**
   * Aplica el branding del negocio. La lógica vive en lib/branding.js, donde
   * además del color escribe los tokens de shadcn, así que los componentes
   * toman el color del negocio solos.
   *
   * Orden de fuentes: vista previa -> base de datos -> localStorage -> colores
   * de Aturno. Antes esto eran ~130 líneas con los mismos 20 setProperty
   * repetidos cuatro veces, uno por cada rama.
   */
  useEffect(() => {
    let cancelado = false;

    // Vista previa: el color llega por props, no hay nada que ir a buscar ni
    // nada que leer de localStorage (todavía no es el branding de nadie).
    if (esPreview) {
      aplicarBranding({
        primaryColor: previewPrimario,
        secondaryColor: previewSecundario,
        accentColor: previewAcento,
      });
      setBrandingListo(true);
      return () => limpiarBranding();
    }

    const cargar = async () => {
      try {
        if (slug) {
          const res = await fetch(`${API_URL}/api/public/business/${slug}/branding`);
          if (res.ok) {
            const branding = await res.json();
            if (!cancelado) {
              aplicarBranding(branding);
              setBrandingListo(true);
            }
            return;
          }
        }
        const guardado = localStorage.getItem('aturno_branding');
        if (!cancelado) {
          aplicarBranding(guardado ? JSON.parse(guardado) : COLORES_POR_DEFECTO);
          setBrandingListo(true);
        }
      } catch (error) {
        console.error('BusinessPage: error aplicando el branding del negocio:', error);
        if (!cancelado) {
          aplicarBranding(COLORES_POR_DEFECTO);
          setBrandingListo(true);
        }
      }
    };

    cargar();

    // Se limpia al salir: los tokens se escriben en documentElement (para que
    // los alcancen los modales, que Radix renderiza en un portal fuera de la
    // página), así que sin esto el color de un negocio se filtraría al resto
    // de la app al navegar.
    return () => {
      cancelado = true;
      limpiarBranding();
    };
    // Los colores de la vista previa entran como strings sueltos a propósito:
    // el onboarding pasa `datosPreview` como objeto literal, o sea con identidad
    // nueva en cada render, y el efecto volvería a correr de gusto.
  }, [slug, esPreview, previewPrimario, previewSecundario, previewAcento]);

  // useEffect para cargar los datos del negocio desde el backend
  useEffect(() => {
    // Vista previa: los datos ya están en memoria, no hay nada que cargar.
    //
    // Se aplana `businessInfo` también arriba de todo porque los componentes de
    // esta página leen las dos formas (`businessData.businessInfo?.x` y
    // `businessData.x`), igual que la respuesta de la API.
    if (esPreview) {
      setBusinessData({
        ...datosPreview.businessInfo,
        businessInfo: datosPreview.businessInfo || {},
        services: datosPreview.services || [],
        staff: datosPreview.staff || [],
        resources: datosPreview.resources || [],
        schedule: datosPreview.schedule || {},
        branding: datosPreview.branding || {},
        // La imagen va como clave propia y explícita, después del spread.
        //
        // Colgaba de que `businessInfo` la trajera adentro, que es cierto en el
        // onboarding pero no tiene por qué serlo en el panel: ahí `businessInfo`
        // es el formulario de datos del negocio —nombre, teléfono, dirección— y
        // la imagen es un estado aparte. Quien arma la vista previa la manda
        // suelta y acá se acepta de las dos formas, así que probar una imagen
        // nueva se ve en el momento venga de donde venga.
        logo: datosPreview.logo ?? datosPreview.businessInfo?.logo ?? null,
        profileImage: datosPreview.profileImage ?? datosPreview.businessInfo?.profileImage ?? null,
      });
      setError(null);
      setLoading(false);
      return;
    }

    const fetchBusinessData = async () => {
      setLoading(true);
      setError(null);

      // En modo demo, usar datos hardcodeados
      if (isDemo) {
        const demoData = {
          id: 'demo-business',
          name: 'Peluquería StyleDemo',
          description: 'Esta es una peluquería de demostración para que pruebes cómo funciona Aturno',
          // Sin logo a propósito. Acá había `/api/placeholder/200/200` y
          // `/api/placeholder/800/400`, rutas que no existen en el backend: la
          // demo pedía dos imágenes que devolvían 404, y el `og:image` de la
          // página apuntaba a una de ellas, así que la preview al compartirla
          // salía rota. `coverImage` además no lo lee nadie.
          //
          // Sin logo, HeroNegocio muestra las iniciales del negocio, que es
          // exactamente lo que ve un negocio real que todavía no subió el suyo:
          // la demo muestra el producto de verdad y no una imagen inventada.
          slug: 'demo-business',
          // Los servicios llevan staffSelectionMode: 'required' y
          // requireResourceSelection: true a propósito, para que la demo
          // recorra los cuatro pasos: servicio -> profesional -> lugar -> horario.
          services: [
            {
              id: 'service-1',
              name: 'Corte de pelo',
              duration: 30,
              price: 1500,
              description: 'Corte profesional con lavado incluido',
              enableDynamicPricing: false,
              active: true,
              staffSelectionMode: 'required',
              requireResourceSelection: true,
            },
            {
              id: 'service-2',
              name: 'Corte + barba',
              duration: 45,
              price: 2000,
              description: 'Corte de pelo y arreglo de barba',
              enableDynamicPricing: false,
              active: true,
              staffSelectionMode: 'required',
              requireResourceSelection: true,
            },
            {
              id: 'service-3',
              name: 'Coloración',
              duration: 120,
              price: 5000,
              description: 'Coloración completa con productos premium',
              enableDynamicPricing: false,
              active: true,
              staffSelectionMode: 'required',
              requireResourceSelection: true,
            }
          ],
          // Cada profesional necesita su PROPIO horario: cuando el cliente elige
          // uno, BookingCalendar calcula la disponibilidad con
          // staff.schedule[dia].ranges y devuelve vacío si no existe. Sin esto
          // la demo no mostraba ni un horario libre.
          staff: [
            {
              id: 'staff-1',
              name: 'Juan Pérez',
              active: true,
              services: ['service-1', 'service-2', 'service-3'],
              schedule: horarioDemo,
            },
            {
              id: 'staff-2',
              name: 'María González',
              active: true,
              services: ['service-1', 'service-3'],
              schedule: horarioDemo,
            }
          ],
          // Los "lugares" del negocio. No existían, así que el paso de elegir
          // lugar nunca aparecía en la demo.
          resources: [
            {
              id: 'resource-1',
              name: 'Sillón 1',
              description: 'Junto a la ventana',
              active: true,
            },
            {
              id: 'resource-2',
              name: 'Sillón 2',
              description: 'Zona central',
              active: true,
            },
            {
              id: 'resource-3',
              name: 'Cabina privada',
              description: 'Para coloración y tratamientos',
              active: true,
            }
          ],
          // La forma correcta es { enabled, ranges: [{ start, end }] }, que es
          // la que guarda el onboarding y la que lee BookingCalendar. Acá había
          // { isOpen, openTime, closeTime, breaks }, que no la entiende nadie:
          // por eso la demo no mostraba ni un horario disponible.
          schedule: horarioDemo,
          settings: {
            depositRequired: false,
            allowCancellations: true,
            cancellationHours: 24,
            enableOnlinePayments: false,
            enableWhatsApp: false,
            bookingLeadTime: 0,
            bookingWindowDays: 30
          },
          /* La terna de Aturno, importada y no escrita a mano. Antes decía
             #667eea / #764ba2 / #f093fb, tres colores que no existen en la
             paleta: el negocio de la demostración se pintaba de violeta y su
             panel salía de un color distinto al de esta misma página. */
          branding: { ...COLORES_POR_DEFECTO }
        };

        setBusinessData(demoData);
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/public/business/${slug}`);
        if (response.status === 404) {
          throw new Error("El negocio que buscas no existe.");
        }
        if (!response.ok) {
          throw new Error("No se pudo cargar la información del negocio.");
        }
        const data = await response.json();
        // Solo mostrar datos iniciales una vez
        if (!businessData) {
        
          // 🔍 DEBUG: Verificar servicios con precios dinámicos
          if (data.services) {
            data.services.forEach(service => {
              
              // 🔍 DEBUG DETALLADO: Examinar cada variación
              if (service.priceVariations && service.priceVariations.length > 0) {
                service.priceVariations.forEach((variation, index) => {
          });
              }
            });
          }
        }
        
        setBusinessData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug || isDemo) {
      fetchBusinessData();
    }
    // Se ejecuta cada vez que cambia el slug de la URL, isDemo, o los datos de
    // la vista previa (el onboarding la re-renderiza a medida que el negocio
    // completa los pasos).
  }, [slug, isDemo, esPreview, datosPreview]);

  // Auto-seleccionar personal/recursos cuando cambie el servicio seleccionado
  useEffect(() => {
    if (selectedService && businessData) {
      autoSelectSingleOptions();
    }
  }, [selectedService, businessData]);

  /**
   * Corta cualquier acción que implique reservar o gestionar un turno cuando la
   * página se está mirando en vista previa. Devuelve true si cortó, así el que
   * llama hace `if (bloqueadoPorPreview()) return;`.
   */
  const bloqueadoPorPreview = () => {
    if (!esPreview) return false;
    toast.info('Es una vista previa', {
      description: 'Cuando publiques tu página, acá tus clientes van a poder reservar.',
    });
    return true;
  };

  const handleSelectSlot = (data) => {
    if (bloqueadoPorPreview()) return;

    // 🔄 Si estamos reprogramando, manejar directamente la reprogramación
    if (isRescheduling && currentBooking) {
      handleRescheduleSubmit(data);
      return;
    }
    
    // El precio de este turno y, si no es el de lista, por qué.
    const selectedDate = new Date(data.date);
    const precio = calcularPrecio(data.service, selectedDate, data.time);
    const dynamicPrice = precio.precio;
    const priceMultiplier = precio.base ? dynamicPrice / precio.base : 1;
    
    
    // Flujo normal de reserva
    // 🔧 CORREGIDO: Usar el personal del slot si no hay selección manual
    const finalStaff = selectedStaff || data.staff;
    const finalResource = selectedResource || data.resource;
    
    
    const enrichedData = {
      ...data,
      selectedStaff: finalStaff,
      selectedResource: finalResource,
      dynamicPrice: dynamicPrice,
      priceMultiplier: priceMultiplier,
      // El motivo viaja armado y no como la variación cruda: el que lo muestra
      // no tiene por qué volver a decidir cómo se llama un precio.
      motivoDelPrecio: precio.motivo,
      cuandoRigeElPrecio: precio.cuando
    };
    
    
    setBookingData(enrichedData);
    setShowEmptyCard(true);
    
    // Auto-scroll con delay para que el componente se renderice primero
    setTimeout(() => {
      const emptyCardSection = document.querySelector('.empty-card-section');
      if (emptyCardSection) {
        emptyCardSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  /**
   * Elegir servicio abre el paso siguiente.
   *
   * Antes acá había una copia de la lógica de "¿hace falta elegir profesional?"
   * y "¿hace falta elegir lugar?", escrita distinto de la que decide qué
   * secciones se dibujan, más dos setTimeout encadenados para adivinar cuándo
   * estaba el DOM listo. Ahora se le pregunta a `pasoSiguiente`, que es la misma
   * cuenta que usa el render, y del momento se encarga el efecto de arriba.
   */
  const handleServiceSelect = (service) => {
    setSelectedService(service);

    // Limpiar selecciones previas de personal/recursos y volver a resolver lo
    // que este servicio elige solo.
    const { profesional, recurso } = seleccionAutomatica(service);
    setSelectedStaff(profesional);
    setSelectedResource(recurso);

    // 'servicio' no es una sección: es el paso desde el que se viene, y como no
    // está en la lista de secciones, `pasoSiguiente` devuelve la primera.
    pasoPendiente.current = pasoSiguiente(service, 'servicio', { profesional, recurso });
  };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);

    // El lugar va DESPUÉS del profesional, no antes ni salteado. Acá se iba
    // derecho al calendario: cuando el servicio también pedía elegir lugar, esa
    // sección todavía no existía en el DOM, el scroll no encontraba a dónde ir y
    // el paso del lugar aparecía abajo sin que nada lo señalara.
    pasoPendiente.current = pasoSiguiente(selectedService, 'staff-section',
      { profesional: staff, recurso: selectedResource });
  };

  const handleResourceSelect = (resource) => {
    setSelectedResource(resource);

    pasoPendiente.current = pasoSiguiente(selectedService, 'resource-section');
  };

  const handleConfirmBooking = (bookingInfo) => {
    // Red de contención: en vista previa nunca se llega hasta acá, porque
    // handleSelectSlot ya cortó antes de abrir el formulario.
    if (bloqueadoPorPreview()) return;

    // Determinar el modo de selección de staff
    const staffMode = bookingInfo.service?.staffSelectionMode || 
                     (bookingInfo.service?.requireStaffSelection ? 'required' : 'automatic');
    
    
    // Si se seleccionó "Sin preferencia" O el modo es automático, asignar un profesional random
    let finalStaff = bookingInfo.selectedStaff;
    let wasAutoAssigned = false;
    
    if (bookingInfo.selectedStaff?.isAnyStaff || bookingInfo.selectedStaff?.id === 'any' || staffMode === 'automatic') {
      const availableStaff = getAvailableStaff();
      
      if (availableStaff.length === 0) {
        console.error('❌ No hay staff disponible para asignación automática');
        toast.error('No hay profesionales disponibles', { description: 'Probá con otro horario o elegí un profesional en particular.' });
        return;
      }
      
      // Seleccionar un profesional al azar de los disponibles
      const randomIndex = Math.floor(Math.random() * availableStaff.length);
      finalStaff = availableStaff[randomIndex];
      wasAutoAssigned = true;
      
    } else {
      wasAutoAssigned = false;
    }

    // Crear el objeto de booking completo con el staff final
    const completeBookingInfo = {
      ...bookingInfo,
      selectedStaff: finalStaff,
      wasStaffAutoAssigned: wasAutoAssigned
    };

    // 💰 VERIFICAR SI EL SERVICIO REQUIERE SEÑA
    if (bookingInfo.service.requiresDeposit) {
      setPendingBookingData(completeBookingInfo);
      setShowDepositModal(true);
      setShowEmptyCard(false); // Cerrar la card de reserva
      return;
    }

    // Si no requiere seña, verificar anti-spam antes de confirmar
    checkAntiSpamProtection(completeBookingInfo);
  };

  // 🛡️ FUNCIÓN: Verificar protección anti-spam antes de confirmar reserva
  const checkAntiSpamProtection = async (bookingInfo) => {
    // En vista previa no se postea nada, ni siquiera la verificación.
    if (esPreview) return;

    // En modo demo, saltar la verificación anti-spam
    if (isDemo) {
      proceedWithBookingConfirmation(bookingInfo);
      return;
    }
    
    if (!businessData?.id) {
      // console.warn('🛡️ BusinessData ID no disponible, procediendo sin verificación anti-spam');
      proceedWithBookingConfirmation(bookingInfo);
      return;
    }

    setIsCheckingSpam(true);
    setPendingBookingForSpam(bookingInfo);

    try {
      
      const response = await fetch(`${API_URL}/api/spam-protection/check-ip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId: businessData.id
        }),
      });

      const result = await response.json();
      setSpamCheckResult(result);


      if (response.status === 429) {
        // IP bloqueada
        if (result.permanent) {
          toast.error('No podemos procesar tu reserva', { description: 'Si creés que es un error, comunicate directamente con el negocio.', duration: 10000 });
        } else {
          const hours = result.hoursLeft || Math.ceil((result.blockedUntil - Date.now()) / (1000 * 60 * 60));
          toast.error('Alcanzaste el límite de reservas', { description: `Vas a poder reservar de nuevo en ${hours} horas.`, duration: 10000 });
        }
        
        setIsCheckingSpam(false);
        setPendingBookingForSpam(null);
        return;
      }

      if (result.requiresCaptcha) {
        // Segunda reserva: mostrar captcha
        setShowCaptcha(true);
        setIsCheckingSpam(false);
        return;
      }

      if (result.allowed) {
        // Primera reserva o captcha ya resuelto: proceder normalmente
        proceedWithBookingConfirmation(bookingInfo);
        setIsCheckingSpam(false);
        setPendingBookingForSpam(null);
        return;
      }

      // Caso no contemplado
      // console.warn('🛡️ Caso no contemplado en anti-spam:', result);
      proceedWithBookingConfirmation(bookingInfo);
      setIsCheckingSpam(false);
      setPendingBookingForSpam(null);

    } catch (error) {
      console.error('🛡️ Error en verificación anti-spam:', error);
      // En caso de error, proceder normalmente (failsafe)
      proceedWithBookingConfirmation(bookingInfo);
      setIsCheckingSpam(false);
      setPendingBookingForSpam(null);
    }
  };

  // 🛡️ FUNCIÓN: Manejar captcha verificado exitosamente
  const handleCaptchaVerified = () => {
    setShowCaptcha(false);
    
    if (pendingBookingForSpam) {
      proceedWithBookingConfirmation(pendingBookingForSpam);
      setPendingBookingForSpam(null);
    }
  };

  // 🛡️ FUNCIÓN: Manejar cancelación del captcha
  const handleCaptchaCancel = () => {
    setShowCaptcha(false);
    setPendingBookingForSpam(null);
    // Volver a mostrar la EmptyServiceCard para que puedan intentar de nuevo
    setShowEmptyCard(true);
  };

  // 🛡️ FUNCIÓN: Registrar reserva exitosa para incrementar contador
  const logBookingSuccess = async (bookingId) => {
    if (!businessData?.id || isDemo || esPreview) return;

    try {
      await fetch(`${API_URL}/api/spam-protection/log-booking-success`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId: businessData.id,
          bookingId: bookingId
        }),
      });
    } catch (error) {
      console.error('🛡️ Error registrando reserva en anti-spam:', error);
    }
  };

  // 💰 NUEVA FUNCIÓN: Crear reserva en backend cuando hay seña de MercadoPago
  const createBookingWithDeposit = async (bookingInfo) => {
    if (bloqueadoPorPreview()) return null;

    try {
      // 🔧 ARREGLO TEMPORAL: SIEMPRE usar Argentina
      let clientTimeZone = 'America/Argentina/Buenos_Aires';
      
      // Preparar datos para el backend
      const bookingData = {
        businessId: businessData.id,
        service: {
          id: bookingInfo.service.id,
          name: bookingInfo.service.name,
          price: bookingInfo.dynamicPrice || bookingInfo.service.price,
          duration: bookingInfo.service.duration,
          requiresDeposit: bookingInfo.service.requiresDeposit,
          depositConfig: bookingInfo.service.depositConfig
        },
        date: bookingInfo.date.toISOString().split('T')[0], // YYYY-MM-DD
        time: bookingInfo.time,
        /**
         * El cliente entero, no tres campos elegidos a mano.
         *
         * ACÁ ESTABA EL BUG de los datos de paciente. Esto decía:
         *
         *     customer: { name: …, phone: …, email: … }
         *
         * o sea una lista blanca de tres claves. El formulario mandaba además
         * `dni`, `fechaDeNacimiento`, `obraSocial` y `numeroDeAfiliado` —y
         * `aceptaWhatsApp`, que también se perdía—, y esta línea los tiraba a la
         * basura antes del POST. Por eso el turno se creaba sin DNI aunque el
         * campo estuviera completo, y por eso la búsqueda del turno siguiente
         * nunca encontraba a nadie: no había un solo turno con DNI guardado.
         *
         * Es el mismo patrón que documenta CLAUDE.md para POST /api/services:
         * una lista blanca que se queda atrás del formulario y descarta en
         * silencio lo que no conocía cuando se escribió. La forma de que no
         * vuelva a pasar es no enumerar: el email queda con su `|| ''` porque el
         * backend lo espera como string, y el resto viaja tal como se cargó.
         */
        customer: {
          ...bookingInfo.customer,
          email: bookingInfo.customer.email || ''
        },
        selectedStaff: bookingInfo.selectedStaff,
        selectedResource: bookingInfo.selectedResource,
        dynamicPrice: bookingInfo.dynamicPrice,
        priceMultiplier: bookingInfo.priceMultiplier || 1,
        depositInfo: bookingInfo.depositInfo,
        clientTimeZone: clientTimeZone // Enviar la zona horaria del cliente
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();

      if (response.ok) {
        
        // Mostrar confirmación con estado de seña pendiente
        const reservation = {
          service: bookingInfo.service.name,
          date: bookingInfo.date.toLocaleDateString('es-AR'),
          time: bookingInfo.time + ' hs',
          price: `$${bookingInfo.dynamicPrice || bookingInfo.service.price}`,
          customerName: bookingInfo.customer.name,
          customerPhone: bookingInfo.customer.phone,
          businessName: businessData.businessInfo?.name || businessData.name || 'Negocio',
          code: data.code,
          staff: bookingInfo.selectedStaff,
          resource: bookingInfo.selectedResource,
          depositInfo: bookingInfo.depositInfo,
          status: 'pending_payment',
          statusMessage: '⏳ Pendiente de pago de seña',
          // Datos crudos para el botón de agregar al calendario.
          fechaISO: aFechaISO(bookingInfo.date),
          hora: bookingInfo.time,
          duracionMinutos: bookingInfo.service.duration
        };

        setReservationDetails(reservation);
        setShowEmptyCard(false);

        // Con Mercado Pago NO se muestra la confirmación acá: el turno todavía
        // no es de nadie. La reserva existe apartando el horario 30 minutos
        // mientras la persona paga, y recién el webhook la confirma.
        //
        // Mostrar la pantalla de "turno reservado" con su código antes de
        // cobrar era decirle a alguien que ya tenía el turno para después
        // mandarlo a pagar: el que no completaba el pago se iba convencido de
        // tener un turno que se le iba a vencer sin que nadie le avisara.
        // El momento de "ahí te da el turno" es la vuelta del pago
        // (/payment-success), no este.
        if (bookingInfo.depositInfo?.paymentMethod?.id !== 'mercadopago') {
          setShowDepositModal(false); // Cerrar modal de depósito
          setConfirmationModal(true);
          setShowConfirmation(true);
        }
        setRefreshTrigger(prev => prev + 1);

        // Devuelve la respuesta entera —`{ bookingId, code, assignedResource }`—
        // y no `data.booking`, que no existe en esa respuesta: devolvía
        // `undefined` incluso cuando la reserva se había creado bien. El pago de
        // la seña con Mercado Pago necesita el `bookingId` para pedir el link.
        return data;
      } else {
        // `details` es donde el validador del backend dice QUÉ campo rechazó.
        // Sin esto sólo llegaba "Datos de reserva inválidos", que fue
        // exactamente por qué costó tanto encontrar que el campo era
        // `service.name`: el motivo existía y se tiraba a la basura.
        console.error('❌ El backend rechazó la reserva:', response.status, data);
        throw new Error(data.error || data.message || 'Error al crear la reserva');
      }
    } catch (error) {
      console.error('❌ Error creando reserva con seña:', error);
      // Esto escribía en `managementError`, que es el estado de error del modal
      // de gestionar un turno y sólo se renderiza adentro de ese modal (dos
      // veces, más abajo). Pero acá el que está en pantalla es el diálogo de la
      // seña, así que el mensaje no lo veía nadie: se apretaba Continuar y la
      // pantalla se quedaba igual, sin error y sin turno. El toast se ve
      // siempre, esté abierto el diálogo que esté.
      toast.error('No pudimos crear tu turno', {
        // El motivo del backend va en el mensaje y no un texto fijo: un
        // "Este horario ya fue reservado" se resuelve solo eligiendo otra hora,
        // y decirle a esa persona "probá de nuevo en un momento" la deja
        // apretando el mismo botón contra el mismo horario ocupado.
        description: error?.message
          ? `${error.message}. Si sigue fallando, escribinos por WhatsApp.`
          : 'Probá de nuevo en un momento. Si sigue fallando, escribinos por WhatsApp.',
        duration: 8000
      });
      return null;
    }
  };

  const proceedWithBookingConfirmation = async (bookingInfo) => {
    
    // En modo demo, usar el contexto demo
    if (isDemo) {
      const demoBooking = {
        date: bookingInfo.date.toISOString().split('T')[0],
        time: bookingInfo.time,
        serviceName: bookingInfo.service.name,
        serviceId: bookingInfo.service.id,
        staffName: bookingInfo.selectedStaff?.name || 'Sin asignar',
        staffId: bookingInfo.selectedStaff?.id,
        // El lugar elegido no se guardaba, así que el panel no podía mostrarlo
        resourceName: bookingInfo.selectedResource?.name || null,
        resourceId: bookingInfo.selectedResource?.id || null,
        customerName: bookingInfo.customer.name,
        customerEmail: bookingInfo.customer.email,
        customerPhone: bookingInfo.customer.phone,
        duration: bookingInfo.service.duration,
        price: bookingInfo.service.price,
        code: `DEMO-${Math.random().toString(36).substring(2, 6).toUpperCase()}`
      };
      
      if (addDemoBooking) {
        addDemoBooking(demoBooking);
      }
      bookingInfo.code = demoBooking.code;
    } else {
      // Agregar la reserva al contexto local
      const fechaStr = bookingInfo.date.toISOString().split('T')[0]; // YYYY-MM-DD
      const horaStr = bookingInfo.time; // HH:mm
      
      addReserva({
        fecha: fechaStr,
        hora: horaStr,
        servicioId: bookingInfo.service.name, // Usar el nombre del servicio
        cliente: {
          nombre: bookingInfo.customer.name,
          telefono: bookingInfo.customer.phone
        }
      });
    }

      const formatDate = (date) => {
        return new Intl.DateTimeFormat('es-AR', {
          day: 'numeric',
          month: 'long'
        }).format(date);
      };

      const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-AR', {
          style: 'currency',
          currency: 'ARS',
          minimumFractionDigits: 0
        }).format(amount);
      };
      
      // Usar precio dinámico si está disponible
      const finalPrice = bookingInfo.dynamicPrice || bookingInfo.service.price;

      const reservation = {
        service: bookingInfo.service.name,
        date: formatDate(bookingInfo.date),
        time: bookingInfo.time + ' hs',
        price: formatCurrency(finalPrice),
        priceMultiplier: bookingInfo.priceMultiplier,
        customerName: bookingInfo.customer.name,
        customerPhone: bookingInfo.customer.phone,
        businessName: businessData.businessInfo?.name || businessData.name || 'Negocio',
        code: bookingInfo.code, // Agregar el código de reserva
        staff: bookingInfo.selectedStaff, // Usar el staff ya procesado
        resource: bookingInfo.selectedResource,
        wasStaffAutoAssigned: bookingInfo.wasStaffAutoAssigned,
        // 💰 Información de seña si aplica
        depositInfo: bookingInfo.depositInfo || null,
        // Datos crudos para el botón de agregar al calendario: `date` y `time`
        // de acá arriba ya vienen formateados para mostrar.
        fechaISO: aFechaISO(bookingInfo.date),
        hora: bookingInfo.time,
        duracionMinutos: bookingInfo.service.duration
      };

      // 🛡️ Registrar reserva exitosa para el sistema anti-spam
      if (bookingInfo.code) {
        await logBookingSuccess(bookingInfo.code);
      }

      setReservationDetails(reservation);
      setShowEmptyCard(false);
      setConfirmationModal(true);
      setShowConfirmation(true);
      
      // Forzar recarga del calendario
      setRefreshTrigger(prev => prev + 1);
  };

  // 💰 MANEJAR SELECCIÓN DE MÉTODO DE PAGO DE SEÑA
  /**
   * El precio de este turno y por qué.
   *
   * La cuenta vive en lib/precios.js: acá quedaba escrita adentro del
   * componente, así que para contarle al cliente el motivo del precio había que
   * repetir el recorrido de las franjas en cada lugar que lo mostrara.
   */
  const calcularPrecio = (service, date, time) => precioDelTurno(service, date, time);

  const handleDepositPaymentSelected = async (paymentInfo) => {
    
    // Seña con Mercado Pago: se crea la reserva como pendiente de pago, se pide
    // el link con el token del propio negocio y se manda al cliente a pagar.
    //
    // El plan original era al revés —crear la reserva recién cuando el pago
    // confirmara— y por eso este camino quedó sin implementar y el cliente veía
    // "todavía no está disponible" para una función anunciada en la landing.
    // Crear primero es lo que hace que funcione con lo que ya existe: el backend
    // deja la reserva en `pending_payment` con el link vencido a los 30 minutos,
    // y el webhook la confirma cuando el pago entra.
    if (paymentInfo.paymentMethod?.id === 'mercadopago') {
      const datosConSeña = { ...pendingBookingData, depositInfo: paymentInfo };
      setPendingBookingData(datosConSeña);

      const reserva = await createBookingWithDeposit(datosConSeña);
      if (!reserva?.bookingId) {
        // createBookingWithDeposit ya mostró el error.
        return;
      }

      try {
        const respuesta = await fetch(`${API_URL}/api/payments/mercadopago/create-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookingId: reserva.bookingId,
            businessId: businessData.id,
            depositAmount: paymentInfo.depositAmount,
            description: `Seña - ${datosConSeña.service?.name || 'Turno'}`,
            customerName: datosConSeña.customer?.name || '',
            customerEmail: datosConSeña.customer?.email || '',
            customerPhone: datosConSeña.customer?.phone || ''
          })
        });

        const datos = await respuesta.json();

        if (!respuesta.ok || !datos.paymentLink) {
          throw new Error(datos.message || datos.error || 'No se recibió el link de pago');
        }

        window.location.href = datos.paymentLink;
      } catch (error) {
        console.error('❌ Error generando el link de pago de la seña:', error);
        // La reserva YA existe y quedó pendiente de pago, así que lo peor que se
        // puede hacer acá es decir "no se pudo" a secas: el turno está tomado.
        toast.error('No pudimos abrir el pago de la seña', {
          description: `Tu turno quedó reservado con el código ${reserva.code}, pendiente de pago. Comunicate con el negocio para completar la seña.`,
          duration: 12000
        });
      }

      return;
    }

    // Para otros métodos de pago (efectivo, transferencia)
    
    const bookingWithDeposit = {
      ...pendingBookingData,
      depositInfo: paymentInfo
    };
    
    // El diálogo NO se cierra acá. `createBookingWithDeposit` lo cierra él mismo
    // cuando la reserva sale bien, junto con abrir la confirmación. Cerrarlo
    // antes de saber el resultado dejaba a la persona sin turno y sin diálogo
    // —la pantalla volvía a la página como si no hubiera pasado nada— y sin
    // dónde reintentar. Si falla, el diálogo sigue abierto y el toast explica.
    await createBookingWithDeposit(bookingWithDeposit);
  };

  // 💰 CANCELAR MODAL DE SEÑA
  const handleDepositCancel = () => {
    setShowDepositModal(false);
    setPendingBookingData(null);
    // Volver a mostrar la EmptyServiceCard para que puedan intentar de nuevo
    setShowEmptyCard(true);
  };

  /**
   * "Entendido" cierra la confirmación y RECARGA la página.
   *
   * Es el único lugar del flujo donde recargar no cuesta nada: el turno ya está
   * guardado, el código ya se mostró y ya salió por mail, y no queda ningún
   * formulario a medio llenar que se pueda perder. A cambio, la página vuelve
   * desde cero — con el horario recién tomado ya fuera de la grilla, los pasos
   * cerrados y sin nada de la reserva anterior colgado en el estado.
   *
   * Sin esto quedaba la pantalla del turno recién sacado con el resto del estado
   * a medias, y era el camino más corto a sacar dos turnos seguidos sin querer.
   *
   * En la vista previa NO se recarga: ahí este componente se monta adentro del
   * panel o del onboarding, y un `reload()` se llevaría puesta la pantalla del
   * dueño del negocio —con sus cambios sin guardar— por haber apretado
   * "Entendido" en una maqueta.
   */
  const handleCloseConfirmation = () => {
    setConfirmationClosing(true);
    setTimeout(() => {
      setShowConfirmation(false);
      setConfirmationClosing(false);

      if (!esPreview) {
        window.location.reload();
      }
    }, 350); // Duración de la animación en ms
  };

  const handleCancelEmptyCard = () => {
    setShowEmptyCard(false);
    setBookingData(null);
  };

  const handleManageBooking = () => {
    // Todavía no existe el negocio: no hay ningún turno que buscar.
    if (bloqueadoPorPreview()) return;

    setShowManageModal(true);
    setManagementStep("search");
    setFoundBooking(null);
    setManagementCode("");
    setManagementError("");
    setIsProcessing(false);
  };

  const handleCodeSubmit = async () => {
    if (!managementCode.trim()) {
      setManagementError("Por favor ingresa tu código de reserva");
      return;
    }

    setIsProcessing(true);
    setManagementError("");

    try {
      const response = await fetch(`${API_URL}/api/bookings/by-code/${encodeURIComponent(managementCode.trim())}?businessId=${businessData?.id || ''}`);
      const data = await response.json();

      if (!response.ok) {
        setManagementError(data.error || "No se encontró ninguna reserva con ese código");
        setIsProcessing(false);
        return;
      }

      setFoundBooking(data.booking);
      setManagementStep("options");
      setIsProcessing(false);

    } catch (error) {
      console.error("Error al buscar reserva:", error);
      setManagementError("Error de conexión. Intenta de nuevo.");
      setIsProcessing(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!foundBooking) return;

    setIsProcessing(true);
    setManagementError("");

    try {
      const response = await fetch(`${API_URL}/api/bookings/by-code/${encodeURIComponent(foundBooking.code)}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          minHoursAdvance: 2, // 2 horas de anticipación mínima
          businessId: businessData?.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setManagementError(data.error || "No se pudo cancelar la reserva");
        setIsProcessing(false);
        return;
      }

      // Mostrar confirmación de cancelación
      toast.success('Turno cancelado', { description: 'Te enviamos un email con la confirmación.' });
      
      // Cerrar modal y limpiar estados
      setShowManageModal(false);
      setManagementStep("search");
      setFoundBooking(null);
      setManagementCode("");
      setIsProcessing(false);

      // Refrescar el calendario para quitar la reserva cancelada
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error("Error al cancelar reserva:", error);
      setManagementError("Error de conexión. Intenta de nuevo.");
      setIsProcessing(false);
    }
  };

  const handleRescheduleBooking = () => {
    if (!foundBooking) return;
    
    // 🔄 NUEVO: Redireccionar a BusinessPage con parámetros de reprogramación
    const currentUrl = window.location.pathname; // Mantenemos la misma URL base
    navigate(`${currentUrl}?reschedule=${foundBooking.code}`);
    
    // Cerrar el modal de gestión
    setShowManageModal(false);
    setManagementStep("search");
    setFoundBooking(null);
    setManagementCode("");
    setManagementError("");
  };

  const resetManageModal = () => {
    setShowManageModal(false);
    setManagementStep("search");
    setFoundBooking(null);
    setManagementCode("");
    setManagementError("");
    setIsProcessing(false);
  };

  // 🔧 NUEVA FUNCIÓN: Formatear fecha sin problemas de timezone
  const formatDateSafely = (dateString) => {
    // Evitar problemas de timezone interpretando la fecha como local
    const [year, month, day] = dateString.split('-');
    const localDate = new Date(year, month - 1, day); // month - 1 porque los meses van de 0 a 11
    
    return localDate.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // 🔧 NUEVA FUNCIÓN: Reprogramar reserva con calendario funcional
  const handleRescheduleSubmit = async (newBookingInfo) => {
    if (!foundBooking) return;

    setIsProcessing(true);
    setManagementError("");

    try {
      const response = await fetch(`${API_URL}/api/bookings/by-code/${encodeURIComponent(foundBooking.code)}/reschedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newDate: newBookingInfo.date.toISOString().split('T')[0], // YYYY-MM-DD
          newTime: newBookingInfo.time,
          minHoursAdvance: 2,
          businessId: businessData?.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setManagementError(data.error || "No se pudo reprogramar la reserva");
        setIsProcessing(false);
        return;
      }

      // Mostrar confirmación de reprogramación
      toast.success('Turno reprogramado', { description: `Nueva fecha: ${formatDateSafely(newBookingInfo.date.toISOString().split('T')[0])} a las ${newBookingInfo.time} hs.` });
      
      // Cerrar modal y limpiar estados
      setShowManageModal(false);
      setManagementStep("search");
      setFoundBooking(null);
      setManagementCode("");
      setIsProcessing(false);

      // Refrescar el calendario para mostrar los cambios
      setRefreshTrigger(prev => prev + 1);

    } catch (error) {
      console.error("Error al reprogramar reserva:", error);
      setManagementError("Error de conexión. Intenta de nuevo.");
      setIsProcessing(false);
    }
  };

  // Funciones para obtener personal y recursos disponibles para el servicio seleccionado
  //
  // Reciben el servicio en vez de leerlo del estado para que se las pueda llamar
  // con el que se acaba de elegir, antes de que React vuelva a pintar. Es lo que
  // permite decidir cuál es el paso siguiente en el mismo click.
  const getAvailableStaff = (servicio = selectedService) => {
    if (!servicio) {
      return [];
    }

    // Si no hay datos de staff desde el backend, usar datos de fallback para demostración
    if (!businessData?.staff || businessData.staff.length === 0) {
      return [
        { id: 'demo1', name: "Personal Disponible", active: true, description: "Configura tu personal en el Dashboard para personalizar esta información" }
      ];
    }

    const staffData = businessData.staff;
    let resultStaff = [];

    // Si el servicio tiene personal asignado específicamente
    if (servicio.assignedStaff && servicio.assignedStaff.length > 0) {
      resultStaff = staffData.filter(staff => {
        const isActive = staff.active;
        const isAssigned = servicio.assignedStaff.includes(staff.id);
        return isActive && isAssigned;
      });
    } else {
      resultStaff = staffData.filter(staff => {
        return staff.active;
      });
    }
    
    // 🔧 DEDUPLICAR: Eliminar personal duplicado basándose en el ID
    const uniqueStaff = resultStaff.filter((staff, index, self) => 
      index === self.findIndex(s => s.id === staff.id)
    );
    
    return uniqueStaff;
  };

  const getAvailableResources = (servicio = selectedService) => {
    if (!servicio) return [];

    // Si no hay datos de resources desde el backend, usar datos de fallback para demostración
    if (!businessData?.resources || businessData.resources.length === 0) {
      return [
        { id: 'demo1', name: "Lugar Disponible", active: true, description: "Configura tus lugares en el Dashboard para personalizar esta información" }
      ];
    }

    const resourceData = businessData.resources;
    let resultResources = [];

    // Si el servicio tiene recursos asignados específicamente
    if (servicio.assignedLocation && servicio.assignedLocation.length > 0) {
      resultResources = resourceData.filter(resource => {
        const isActive = resource.active;
        const isAssigned = servicio.assignedLocation.includes(resource.id);
        return isActive && isAssigned;
      });
    } else {
      // Si no tiene recursos asignados específicamente, devolver todos los recursos activos
      resultResources = resourceData.filter(resource => resource.active);
    }
    
    // 🔧 DEDUPLICAR: Eliminar recursos duplicados basándose en el ID
    const uniqueResources = resultResources.filter((resource, index, self) => 
      index === self.findIndex(r => r.id === resource.id)
    );
    
    return uniqueResources;
  };

  // Funciones para determinar si un paso es necesario
  //
  // Estas dos deciden qué pasos se dibujan Y a cuál se lleva la pantalla después
  // de cada elección. Las dos cosas tienen que salir de la misma cuenta: cuando
  // el auto-scroll tenía su propia copia de esta lógica (pedía DOS profesionales
  // para considerar que había que elegir, mientras que la sección se dibujaba
  // con uno), un servicio con un solo profesional en modo opcional mostraba el
  // paso y mandaba la pantalla al calendario, que todavía no existía: no pasaba
  // nada y el paso nuevo quedaba abajo, fuera de la vista.
  const isStaffSelectionRequired = (servicio = selectedService) => {
    if (!servicio) return false;

    // Usar staffSelectionMode si está disponible, sino usar requireStaffSelection (backward compatibility)
    const staffMode = servicio.staffSelectionMode || (servicio.requireStaffSelection ? 'required' : 'automatic');

    // Si el modo es 'required' o 'optional', mostrar la selección de personal
    if (staffMode === 'required' || staffMode === 'optional') {
      return getAvailableStaff(servicio).length >= 1; // Mostrar si hay al menos un profesional
    }

    /* `alwaysShowStaff` muestra el paso aunque el modo sea automático.
       Lo prende el negocio desde el panel y sirve para el caso de una sola
       persona: ahí no hay nada que elegir, pero sí hay algo que MOSTRAR. En un
       consultorio, con quién te atendés es parte de lo que estás reservando, y
       sin este paso el nombre no aparecía en ningún momento del flujo. */
    if (servicio.alwaysShowStaff && getAvailableStaff(servicio).length >= 1) {
      return true;
    }

    // Si el modo es 'automatic', no mostrar selección (asignación automática)
    return false;
  };

  const isResourceSelectionRequired = (servicio = selectedService) => {
    if (!servicio) return false;

    // Si el servicio requiere que el cliente elija recurso específico
    if (servicio.requireResourceSelection) {
      return getAvailableResources(servicio).length >= 1; // Mostrar si hay recursos disponibles
    }

    // Si no está marcado como requerido, la app asignará automáticamente
    return false;
  };

  /**
   * El orden de la reserva: servicio → profesional → lugar → día y hora.
   *
   * Devuelve el id de la sección que sigue a `desde`, entre las que este
   * servicio realmente muestra. Como el paso del servicio no está en la lista,
   * preguntar por él devuelve el primero, que es lo que se quiere al elegir un
   * servicio.
   *
   * Va por posición en la lista y NO por "cuál está sin completar", que es como
   * estaba y por qué no funcionaba: cuando el servicio pide profesional en modo
   * obligatorio, `seleccionAutomatica` ya deja puesto al primero del equipo, así
   * que el paso figuraba como completo y la pantalla se lo saltaba —se iba al
   * calendario— aunque la sección estuviera ahí, abierta y sin mirar. Que haya
   * algo preseleccionado no quiere decir que no haya que pasar por el paso: hay
   * que pasar igual, justamente para poder cambiarlo.
   */
  /**
   * ¿Este negocio pregunta por la obra social?
   *
   * Sólo los del rubro salud, y sólo si además declararon cuáles atienden desde
   * su panel. Un consultorio que no cargó ninguna no muestra el paso: preguntar
   * sin poder ofrecer opciones sería un paso vacío, y ofrecer una lista nacional
   * completa haría que alguien reserve con una cobertura que el lugar no toma.
   */
  /**
   * Las coberturas que se le ofrecen al paciente, y si hay que pedirle los datos.
   *
   * Dos formas de encenderlo, y con cualquiera alcanza:
   *
   *   1. el negocio tildó coberturas en su panel  -> se ofrecen ésas;
   *   2. el rubro es salud y no tildó ninguna     -> se ofrece la lista completa.
   *
   * La segunda existe porque la primera, sola, dejaba la función apagada hasta
   * que alguien encontrara una pantalla de configuración que no sabía que había
   * que visitar: un consultorio recién creado pedía turnos sin preguntar la
   * cobertura y nada indicaba por qué. Ahora un negocio de salud funciona sin
   * configurar nada, y el panel sirve para ACOTAR la lista a las que realmente
   * atiende, que es una mejora y no un requisito.
   *
   * Un negocio que no es de salud sigue sin pedir nada mientras no tilde ninguna
   * —una peluquería no tiene por qué pedir un DNI—, pero si tilda alguna, puede.
   */
  const esDeSalud = businessData?.businessType === 'health';
  const coberturasElegidas = obrasSocialesDelNegocio(
    businessData?.obrasSociales || [],
    businessData?.obrasSocialesPropias || []
  );
  const coberturasDelNegocio =
    coberturasElegidas.length > 0 ? coberturasElegidas : esDeSalud ? TODAS_LAS_OBRAS : [];

  const pideCobertura = coberturasDelNegocio.length > 0;

  // DIAGNÓSTICO TEMPORAL — sacar cuando esté resuelto.
  // Imprime, en cada render de la página cargada, de qué depende que el
  // formulario pida DNI y cobertura. Si acá `pideCobertura` sale false, el
  // problema está en el dato; si sale true y el formulario igual no los pide,
  // el problema está más abajo (ver los logs de EmptyServiceCard).
  if (businessData) {
  }

  /**
   * Qué pasos se muestran, en orden, y qué número le toca a cada uno.
   *
   * Antes cada paso tenía su número calculado a mano en el JSX, y el del
   * calendario era un ternario anidado de tres ramas (`staff && recurso ? 4 :
   * staff || recurso ? 3 : 2`). Con un paso más eso pasa a cuatro ramas y a
   * tener que tocar tres lugares para agregar uno solo. Acá se declara la
   * secuencia una vez y el número sale de la posición.
   */
  const pasosDeLaReserva = [
    'servicio',
    isStaffSelectionRequired() ? 'profesional' : null,
    isResourceSelectionRequired() ? 'lugar' : null,
    'fecha',
  ].filter(Boolean);

  const numeroDePaso = (id) => pasosDeLaReserva.indexOf(id) + 1;

  const pasoSiguiente = (servicio, desde, yaElegido = {}) => {
    if (!servicio) return null;

    const visibles = [
      isStaffSelectionRequired(servicio) ? 'staff-section' : null,
      isResourceSelectionRequired(servicio) ? 'resource-section' : null,
      'booking-section',
    ].filter(Boolean);

    /* Un paso que ya está contestado no es el paso siguiente.
       Cuando el servicio tiene un solo profesional o un solo lugar, la página
       lo elige sola —eso está bien, no hay nada que decidir— pero la sección
       se dibuja igual, con su opción ya marcada. La cuenta de acá se detenía
       en esa sección y mandaba el scroll ahí: la persona quedaba mirando un
       paso que no le pedía nada, y como no había nada que tocar tampoco salía
       el scroll siguiente. El recorrido se cortaba justo donde debía seguir
       solo.
       Se saltea lo que ya tiene valor y se sigue hasta lo primero que de
       verdad esté esperando algo. La sección salteada no desaparece: queda
       arriba, marcada, y se puede volver a cambiarla. */
    const contestado = {
      'staff-section': Boolean(yaElegido.profesional),
      'resource-section': Boolean(yaElegido.recurso),
    };

    for (let i = visibles.indexOf(desde) + 1; i < visibles.length; i += 1) {
      if (!contestado[visibles[i]]) return visibles[i];
    }
    return 'booking-section';
  };

  // El paso al que hay que ir apenas React lo dibuje. Va en un ref porque no es
  // algo que se muestre: es una orden pendiente entre el click y el próximo
  // render.
  const pasoPendiente = useRef(null);

  const irAlPaso = (id, reintento = 0) => {
    const nodo = document.getElementById(id);

    // La sección puede no estar todavía: se monta con el mismo cambio de estado
    // que dispara esto, pero si algo más la retrasa un cuadro, se reintenta una
    // vez antes de dejarlo pasar en silencio.
    if (!nodo) {
      if (reintento < 3) requestAnimationFrame(() => irAlPaso(id, reintento + 1));
      return;
    }

    // El alto que el paso VA a tener, no el que tiene ahora.
    //
    // Cuando esto corre, la sección está empezando a abrirse y todavía mide casi
    // cero, así que cualquier cuenta hecha sobre ella daría un centro equivocado
    // —y `scrollIntoView({ block: 'center' })` tiene el mismo problema, porque
    // mide en el momento en que se lo llama—. El div de adentro no se comprime
    // con la animación: ese ya ocupa lo que va a ocupar.
    const altoFinal = (nodo.firstElementChild ?? nodo).getBoundingClientRect().height;

    // Centrado en la pantalla: se reparte en partes iguales lo que sobra de
    // ventana. Si el paso es más alto que la pantalla no hay centro posible, así
    // que se lo deja arriba, con el margen que respeta la barra fija.
    const sobrante = window.innerHeight - altoFinal;
    const desplazamiento = sobrante > 0 ? sobrante / 2 : 80;
    const arriba = nodo.getBoundingClientRect().top + window.pageYOffset;
    const destino = Math.max(0, arriba - desplazamiento);

    const lenis = scrollSuave.current;


    if (lenis) {
      // ESTE `resize()` ES EL QUE HACE QUE FUNCIONE. No es una precaución.
      //
      // Lenis recorta el destino contra su propio límite —el alto de la página
      // menos el de la ventana—, y ese límite lo recalcula un ResizeObserver,
      // que llega uno o dos cuadros después de que el DOM cambió. Cuando se
      // elige el servicio, la página venía midiendo exactamente una ventana
      // (límite 0) y el paso nuevo todavía no se contó, así que el destino se
      // recortaba a 0. Y como 0 era el scroll actual, lenis salía por su atajo
      // "ya estoy ahí" sin animar nada ni avisar: la página no se movía y en la
      // consola no aparecía ningún error.
      //
      //   lenis.scrollTo(470) -> clamp(0, 470, 0) -> 0 -> return
      //
      // `resize()` lo obliga a medir de nuevo en el momento.
      lenis.resize();

      // Se le pasa la posición ya calculada y no el elemento: con el elemento,
      // lenis le vuelve a restar el `scroll-margin-top` de la sección y el
      // centrado queda corrido. `force` es para que no lo ignore si en ese
      // momento la inercia está frenada por un modal abierto.
      lenis.scrollTo(destino, { force: true });
      return;
    }

    // En la vista previa del onboarding quien scrollea no es la ventana sino la
    // tarjeta del paso, y `scrollIntoView` es lo único que mueve el contenedor
    // que corresponda. Ahí el centrado es aproximado y alcanza.
    if (esPreview) {
      nodo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Sin inercia —en teléfono, o con "reducir movimiento"— lo hace el navegador.
    window.scrollTo({ top: destino, behavior: 'smooth' });
  };

  /**
   * Llevar la pantalla a los horarios cuando se elige un día, sólo en teléfono.
   *
   * En una pantalla de 844px el calendario entero ocupa casi todo lo visible, así
   * que al tocar un día los horarios aparecen abajo, fuera de cuadro: la lista se
   * llena y no pasa nada en la parte de la pantalla que la persona está mirando.
   * En escritorio no hace falta —calendario y horarios entran juntos— y moverle
   * la página a alguien que ya está viendo lo que necesita es peor que no hacer
   * nada, así que esto no corre ahí.
   */
  const irAHorarios = (nodo) => {
    if (!nodo) return;

    // El corte es el mismo `sm` de Tailwind que usa el resto de esta página para
    // decidir cuándo apila; de ahí para arriba calendario y horarios conviven.
    if (!window.matchMedia('(max-width: 640px)').matches) return;

    const caja = nodo.getBoundingClientRect();

    // Si ya se ven, no se toca nada. Mover la página cuando el destino ya está
    // en pantalla es un salto que la persona no pidió y que le hace perder el
    // punto donde estaba mirando.
    if (caja.top >= 0 && caja.top <= window.innerHeight * 0.5) return;

    // Arriba de todo con un margen chico: los horarios son una lista larga y lo
    // que importa es cuántos entran, no centrarla.
    const destino = Math.max(0, caja.top + window.pageYOffset - 12);

    const lenis = scrollSuave.current;
    if (lenis) {
      // Mismo motivo que en `irAlPaso`: lenis recorta el destino contra un límite
      // que recalcula un cuadro más tarde, y acá la página acaba de crecer con
      // los horarios recién dibujados.
      lenis.resize();
      lenis.scrollTo(destino, { force: true });
      return;
    }

    if (esPreview) {
      nodo.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Con "reducir movimiento" se salta igual, pero de una: lenis ya se apaga
    // solo en ese caso (`useScrollSuave`), y un scroll animado es exactamente lo
    // que esa preferencia pide que no pase.
    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: destino, behavior: quieto ? 'auto' : 'smooth' });
  };

  // Se corre después de pintar, que es cuando la sección nueva ya existe en el
  // DOM. Los dos cuadros de espera son para que el paso alcance a empezar a
  // abrirse: si el scroll sale en el mismo cuadro en que se monta, la sección
  // todavía mide cero y la pantalla queda apuntando al lugar equivocado.
  //
  // No lleva limpieza a propósito. Este efecto corre en CADA render, así que una
  // limpieza que cancelara los cuadros pendientes se comería el scroll: entre
  // que se agenda y que se ejecuta hay repintes garantizados —el calendario se
  // monta y pide sus horarios—, y el primero de ellos lo cancelaría. Los dos
  // `requestAnimationFrame` se disparan una sola vez y `irAlPaso` no hace nada
  // si la sección ya no está en el DOM.
  useEffect(() => {
    if (!pasoPendiente.current) return;

    const destino = pasoPendiente.current;
    pasoPendiente.current = null;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => irAlPaso(destino));
    });
  });

  /**
   * Qué queda elegido solo para un servicio, sin tocar el estado.
   *
   * Se separa del `set...` para poder preguntárselo en el mismo click en que se
   * elige el servicio: hasta que React no vuelva a pintar, `selectedStaff` sigue
   * teniendo el valor viejo, y sin esto no habría forma de saber cuál es el paso
   * siguiente hasta un cuadro después.
   */
  const seleccionAutomatica = (servicio, { profesional = null, recurso = null } = {}) => {
    if (!servicio) return { profesional: null, recurso: null };

    const staffMode = servicio.staffSelectionMode || (servicio.requireStaffSelection ? 'required' : 'automatic');
    const resourceMode = servicio.resourceSelectionMode || (servicio.requireResourceSelection ? 'required' : 'automatic');

    const staffDisponible = getAvailableStaff(servicio);
    const recursosDisponibles = getAvailableResources(servicio);

    /* Con UNA sola persona sí se preselecciona, y no es una excepción caprichosa.
       Cuando hay varias, marcar la primera es elegir por la persona: el primero
       de la lista no es una recomendación, es el que quedó primero por orden de
       carga, y la mayoría sigue de largo sin mirar. Con una sola no hay nada que
       elegir —esa persona es la única posibilidad—, así que dejarla en blanco no
       protege ninguna decisión: sólo esconde con quién se va a atender.

       En un consultorio eso importa de verdad. Saber quién te atiende es parte
       de lo que se está reservando, y preseleccionar al único hace que el dato
       viaje al resumen y a la confirmación, donde se puede leer y verificar. */
    const unicoProfesional = staffDisponible.length === 1 ? staffDisponible[0] : null;
    /* Lo mismo para el lugar, y por el mismo motivo. La rama de `recurso` hacía
       justo lo que el párrafo de arriba dice que no hay que hacer: en modo
       "required" —donde el cliente TIENE que elegir— agarraba
       `recursosDisponibles[0]`. Con dos sillones eso significa que la página
       elige el sillón por el cliente, con el orden de carga como criterio, y
       encima deja el paso resuelto antes de que se vea: el scroll pasa de largo
       una pregunta que nunca se hizo. */
    const unicoRecurso = recursosDisponibles.length === 1 ? recursosDisponibles[0] : null;

    return {
      /* Con varios, elige la persona. Con uno solo, queda puesto.

         Entre varios no se preselecciona nada, en ningún modo: marcar el primero
         de la lista es elegir por la persona con un criterio que ni siquiera es
         un criterio (el orden de carga), y en "required" —donde la persona TIENE
         que elegir— es directamente contradictorio.

         Con uno solo es al revés: no hay decisión que proteger y sí hay un dato
         que mostrar. Ver el comentario de `unicoProfesional`. */
      profesional: unicoProfesional || (staffMode === 'automatic' ? null : profesional),
      /* Mismo criterio que arriba: con uno solo queda puesto, entre varios no se
         elige nada. En modo "automatic" sigue en null — ahí lo resuelve el
         backend y no hay paso que mostrar. */
      recurso: resourceMode === 'automatic' ? null : unicoRecurso || recurso,
    };
  };

  // Función para auto-seleccionar staff/recursos cuando no se requiere selección del cliente
  const autoSelectSingleOptions = (servicio = selectedService) => {
    const elegido = seleccionAutomatica(servicio, {
      profesional: selectedStaff,
      recurso: selectedResource,
    });

    setSelectedStaff(elegido.profesional);
    setSelectedResource(elegido.recurso);

    return elegido;
  };

  // Función para determinar si se puede mostrar el calendario
  const canShowCalendar = () => {
    if (!selectedService) return false;
    
    // Si el servicio requiere selección de personal y no hay uno seleccionado
    if (isStaffSelectionRequired() && !selectedStaff) return false;
    
    // Si el servicio requiere selección de recurso y no hay uno seleccionado
    if (isResourceSelectionRequired() && !selectedResource) return false;
    
    return true;
  };



  // Manejo de estados de carga y error
  // Se espera también al branding: si no, la página se pinta con el naranja de
  // Aturno y salta al color del negocio apenas responde el fetch.
  if (loading || !brandingListo) {
    return <LoadingSpinner type="js" />;
  }

  if (error) {
    return <div className="business-page-error">Error: {error}</div>;
  }

  if (!businessData) {
    return <div className="business-page-error">No se encontró información del negocio.</div>;
  }

  // ---------------------------------------------------------------
  // SEO de la página pública
  //
  // Es la página que cada negocio comparte con sus clientes: el <title> es lo
  // que se ve en Google y el Open Graph es lo que se ve cuando la pegan en
  // WhatsApp. Hasta acá no emitía nada, así que heredaba el title genérico del
  // index.html y el preview salía vacío.
  //
  // En vista previa no se emite nada: no es una página real, y además viviría
  // adentro del onboarding, pisándole el title a esa pantalla.
  // ---------------------------------------------------------------
  /**
   * Los servicios que se muestran, filtrados por lo que se escribió en el
   * buscador. La decisión de qué coincide vive en lib/servicios.js, con el mismo
   * normalizador que usa el resto de la app: ignora tildes, mayúsculas y
   * espacios de más, y busca cada palabra por separado en el nombre y en la
   * descripción.
   */
  const todosLosServicios = businessData.services || [];
  const serviciosVisibles = buscarServicios(todosLosServicios, busquedaDeServicio);
  const hayQueOfrecerBuscador = todosLosServicios.length >= MINIMO_PARA_BUSCAR;

  const nombreNegocio = businessData.businessInfo?.name || businessData.name || 'Negocio';
  const rubroNegocio = businessTemplates.find((t) => t.id === businessData.businessType)?.name;
  const direccionNegocio = businessData.businessInfo?.address || businessData.address || '';
  const slugNegocio = businessData.businessInfo?.slug || businessData.slug || slug || '';
  // La demo no vive en /:slug sino en /demo/business: la canónica tiene que
  // apuntar a la URL que existe de verdad.
  const urlNegocio = isDemo ? 'https://aturno.app/demo/business' : `https://aturno.app/${slugNegocio}`;
  const nombresServicios = (businessData.services || [])
    .map((s) => s.name)
    .filter(Boolean);

  // Sin el rubro en el título: <SEO> le agrega " | Aturno" al final, y con el
  // rubro adentro quedaban dos barras. El rubro va en la descripción.
  const tituloSEO = `${nombreNegocio} — Reservá tu turno online`;

  const descripcionSEO = [
    `Reservá tu turno en ${nombreNegocio}${rubroNegocio ? ` (${rubroNegocio.toLowerCase()})` : ''} online, sin llamar.`,
    nombresServicios.length ? `${nombresServicios.slice(0, 4).join(', ')}.` : '',
    direccionNegocio ? `${direccionNegocio}.` : '',
    'Elegí día y horario y confirmá en el momento.',
  ]
    .filter(Boolean)
    .join(' ');

  // Datos estructurados: los buscadores entienden que esto es un comercio local
  // que toma reservas, no una página cualquiera.
  const schemaNegocio = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: nombreNegocio,
    url: urlNegocio,
    ...(businessData.description ? { description: businessData.description } : {}),
    ...(direccionNegocio
      ? { address: { '@type': 'PostalAddress', streetAddress: direccionNegocio, addressCountry: 'AR' } }
      : {}),
    ...(businessData.businessInfo?.phone || businessData.phone
      ? { telephone: businessData.businessInfo?.phone || businessData.phone }
      : {}),
    ...(businessData.profileImage || businessData.logo
      ? { image: businessData.profileImage || businessData.logo }
      : {}),
    ...(nombresServicios.length
      ? {
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Servicios',
            itemListElement: nombresServicios.map((nombre) => ({
              '@type': 'Offer',
              itemOffered: { '@type': 'Service', name: nombre },
            })),
          },
        }
      : {}),
  };

  return (
    // El padding de abajo deja lugar para la barra fija de resumen, que en el
    // teléfono taparía la última sección.
    <div className={`business-page ${selectedService ? 'pb-28 lg:pb-0' : ''}`}>
      {!esPreview && (
        <SEO
          title={tituloSEO}
          description={descripcionSEO}
          keywords={[
            `turnos ${nombreNegocio}`,
            rubroNegocio ? `turnos ${rubroNegocio.toLowerCase()}` : '',
            'reservar turno online',
            'sacar turno',
            ...nombresServicios.slice(0, 4),
          ]
            .filter(Boolean)
            .join(', ')}
          url={urlNegocio}
          canonical={urlNegocio}
          image={businessData.profileImage || businessData.logo || '/logo-aturno.png'}
          type="website"
          author={nombreNegocio}
          // La demo no es un negocio real: que no la indexen.
          noindex={isDemo}
          schema={schemaNegocio}
        />
      )}

      {/* Aviso de vista previa: discreto pero visible, para que quede claro que
          la página está mostrando datos que todavía no se guardaron y que desde
          acá no se reserva. */}
      {esPreview && (
        <div className="aviso-preview" role="status">
          <span className="aviso-preview-punto" aria-hidden="true" />
          <span>
            <strong>Vista previa</strong>{' '}
            <span className="aviso-preview-detalle">
              — así va a ver tu página tu cliente. Desde acá todavía no se reserva.
            </span>
          </span>
        </div>
      )}

      {/* 🔄 Banner de Reprogramación */}
      {isRescheduling && currentBooking && (
        <div className="reschedule-banner">
          <div className="container">
            <div className="reschedule-info">
              <h3>🔄 Reprogramando tu Turno</h3>
              <p>
                <strong>Turno actual:</strong> {currentBooking.service.name} - {formatDateSafely(currentBooking.date)} a las {currentBooking.time} hs
              </p>
              <p>Selecciona una nueva fecha y hora para tu cita</p>
              <button 
                className="btn-cancel-reschedule"
                onClick={() => {
                  navigate(window.location.pathname); // Quitar parámetros de URL
                  setIsRescheduling(false);
                  setCurrentBooking(null);
                }}
              >
                Cancelar Reprogramación
              </button>
            </div>
          </div>
        </div>
      )}
      
      <HeroNegocio businessData={businessData} onManageBooking={handleManageBooking} />

      {/* Mensaje libre del negocio. Si no escribió ninguno, el componente
          devuelve null y no queda ni el contenedor ni un espacio en blanco.
          Se leen las dos formas que convive en el repo: anidado en
          businessInfo (lo que arma el onboarding) y suelto arriba de todo. */}
      <MensajeNegocio mensaje={businessData.businessInfo?.mensaje || businessData.mensaje} />

      {/* Services Section */}
      <section id="services-section" className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
        <PasoSeccion numero={numeroDePaso('servicio')} titulo="Elegí el servicio" />

        {/* El buscador aparece recién cuando la lista es larga (ver
            MINIMO_PARA_BUSCAR). Con cuatro servicios a la vista, un campo de
            búsqueda es un paso de más antes del único que importa: elegir. */}
        {hayQueOfrecerBuscador && (
          <div className="mb-5 sm:mb-6">
            <label htmlFor="buscador-servicios" className="sr-only">
              Buscar un servicio
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                aria-hidden="true"
              />
              <Input
                id="buscador-servicios"
                type="search"
                value={busquedaDeServicio}
                onChange={(e) => setBusquedaDeServicio(e.target.value)}
                placeholder={`Buscar entre ${todosLosServicios.length} servicios`}
                className="h-11 rounded-xl pl-9"
                autoComplete="off"
              />
            </div>
          </div>
        )}

        {/* Nada encontrado: se dice con qué se buscó y se ofrece volver a la
            lista completa. Sin esta rama la sección queda en blanco y parece que
            el negocio no tiene servicios. */}
        {hayQueOfrecerBuscador && serviciosVisibles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-neutral-200 bg-white px-6 py-10 text-center">
            <p className="text-sm text-neutral-600">
              No encontramos ningún servicio que diga{' '}
              <span className="font-semibold text-neutral-900">“{busquedaDeServicio.trim()}”</span>.
            </p>
            <button
              type="button"
              onClick={() => setBusquedaDeServicio('')}
              className="mt-3 text-sm font-semibold text-primary underline underline-offset-4"
            >
              Ver los {todosLosServicios.length} servicios
            </button>
          </div>
        ) : (
        <SelectorOpciones
          ariaLabel="Servicios disponibles"
          opciones={serviciosVisibles.map((s, i) => ({
            valor: s.name || `servicio-${i}`,
            original: s,
          }))}
          valor={selectedService?.name}
          onSeleccionar={handleServiceSelect}
          renderContenido={(service) => (
            <>
              <h3 className="pr-8 font-semibold text-neutral-900">{service.name}</h3>
              {/* Sin descripción no va ningún párrafo.
                  Acá había un `|| 'Servicio profesional de calidad'`: un negocio
                  que dejaba el campo vacío —que es lo normal, es opcional— veía
                  la misma frase inventada debajo de cada uno de sus servicios,
                  como si la hubiera escrito él. Una descripción de más no es
                  neutra: ocupa el lugar de la que el negocio no quiso poner y le
                  pone palabras en la boca a su página pública.
                  Se compara con `trim()` porque el campo llega como '' desde el
                  panel (server.js guarda `description || ''`), y unos espacios
                  sueltos tampoco son una descripción. */}
              {service.description?.trim() && (
                <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                  {service.description}
                </p>
              )}
              <div className="mt-4 flex items-baseline justify-between gap-3 pt-4 border-t border-neutral-100">
                <span className="text-sm text-neutral-500">{service.duration} min</span>
                {/* Sin precio no se escribe nada.
                    Un servicio a consultar no muestra ni el número ni un cartel
                    en su lugar: el renglón queda con la duración sola. Acá había
                    un "A consultar", y decirlo en la tarjeta es ocupar el lugar
                    del precio para avisar que no hay precio, cuando la ausencia
                    ya se entiende sola.

                    Ojo con el respaldo que había antes: el `$` estaba escrito
                    FUERA de la expresión, así que `|| 'Consultar'` renderizaba
                    "$Consultar". El signo va adentro, con el número. */}
                {!service.priceOnRequest && Boolean(service.price) && (
                  <span className="text-lg font-bold text-primary">
                    {service.enableDynamicPricing && (
                      <span className="mr-1 text-xs font-normal text-neutral-500">Desde</span>
                    )}
                    ${service.price.toLocaleString('es-AR')}
                    {/* La condición era `v.type === 'peak'`, y nada escribe nunca
                        ese campo: las franjas las crea PricingCalendar con id,
                        nombre, días, rangos y precio, así que el aviso no se
                        mostró jamás. Alcanza con que haya alguna franja
                        cargada, que es exactamente cuando el precio varía. */}
                    {service.priceVariations?.length > 0 && (
                      <span
                        className="ml-1 text-sm"
                        title="El precio varía según el horario"
                        aria-label="El precio varía según el horario"
                      >
                        ⚡
                      </span>
                    )}
                  </span>
                )}
              </div>
            </>
          )}
        />
        )}
      </section>

      {/* Los pasos que siguen se abren de a uno, en orden: profesional, lugar y
          recién ahí día y hora. Cuáles aparecen depende del servicio.

          Sin `AnimatePresence`: los pasos ya no animan su entrada ni su salida
          (ver el comentario de PasoDeReserva), así que envolverlos era dejar
          maquinaria de animación alrededor de algo que aparece de una. Cambiar
          de servicio saca el paso que deja de corresponder en el acto, que es
          lo que se busca en un formulario. */}
        {selectedService && isStaffSelectionRequired() && (
          <PasoDeReserva
            key="paso-profesional"
            id="staff-section"
            numero={numeroDePaso('profesional')}
            titulo="Elegí el profesional"
            descripcion={
              selectedService.staffSelectionMode === 'required'
                ? `Elegí quién va a hacer tu ${selectedService.name.toLowerCase()}`
                : `Podés elegir quién va a hacer tu ${selectedService.name.toLowerCase()}, o dejarlo a cargo del negocio`
            }
          >
          {getAvailableStaff().length === 0 ? (
            <Alert>
              <AlertDescription>
                Este servicio todavía no tiene profesionales asignados. El negocio
                puede configurarlos desde su panel.
              </AlertDescription>
            </Alert>
          ) : (
            <SelectorOpciones
              ariaLabel="Profesionales disponibles"
              opciones={[
                // "Sin preferencia" solo existe cuando elegir es opcional
                ...(selectedService.staffSelectionMode === 'optional'
                  ? [
                      {
                        valor: 'any',
                        original: { id: 'any', name: 'Sin preferencia', isAnyStaff: true },
                      },
                    ]
                  : []),
                ...getAvailableStaff().map((s, i) => ({
                  valor: String(s.id ?? `staff-${i}`),
                  original: s,
                })),
              ]}
              valor={selectedStaff?.id != null ? String(selectedStaff.id) : undefined}
              onSeleccionar={handleStaffSelect}
              renderContenido={(staff) => (
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12 shrink-0">
                    {/* La foto es opcional. Si no hay, quedan las iniciales, que
                        es lo que se mostraba siempre hasta ahora. */}
                    {!staff.isAnyStaff && staff.foto && (
                      <AvatarImage src={staff.foto} alt="" className="object-cover" />
                    )}
                    <AvatarFallback
                      className={
                        staff.isAnyStaff
                          ? 'bg-neutral-100 text-neutral-500'
                          : 'bg-primary/10 font-semibold text-primary'
                      }
                    >
                      {staff.isAnyStaff
                        ? '?'
                        : staff.name
                            .split(' ')
                            .map((n) => n.charAt(0))
                            .join('')
                            .substring(0, 2)
                            .toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 pr-6">
                    <h3 className="truncate font-semibold text-neutral-900">{staff.name}</h3>
                    {/* Debajo del nombre iba "Profesional del equipo" para todos,
                        que es relleno: no distingue a nadie de nadie, y en una
                        lista de cinco personas repite el mismo renglón cinco
                        veces. Ahora va lo único que sí los diferencia —sus
                        roles— y, si el negocio no le cargó ninguno, no va nada:
                        una línea de menos se lee mejor que una que no informa.

                        La opción "cualquiera" conserva su texto porque ahí sí
                        explica qué pasa si la elegís. */}
                    {staff.isAnyStaff ? (
                      <p className="mt-0.5 text-sm text-neutral-600">
                        El negocio asigna a quien esté disponible
                      </p>
                    ) : (
                      rolesDe(staff).length > 0 && (
                        <p className="mt-0.5 truncate text-sm text-neutral-600">
                          {rolesDe(staff).join(' · ')}
                        </p>
                      )
                    )}
                  </div>
                </div>
              )}
            />
          )}
          </PasoDeReserva>
        )}

      {/* Resource Selection Section - Only show if service and staff are selected (if required) and resource selection is required */}
        {selectedService && (!isStaffSelectionRequired() || selectedStaff) && isResourceSelectionRequired() && (
          <PasoDeReserva
            key="paso-lugar"
            id="resource-section"
            numero={numeroDePaso('lugar')}
            titulo="Elegí el lugar"
            descripcion={`Dónde se hace tu ${selectedService.name.toLowerCase()}`}
          >
          {getAvailableResources().length === 0 ? (
            <Alert>
              <AlertDescription>
                Este servicio todavía no tiene lugares asignados. El negocio puede
                configurarlos desde su panel.
              </AlertDescription>
            </Alert>
          ) : (
            <SelectorOpciones
              ariaLabel="Lugares disponibles"
              opciones={getAvailableResources().map((r, i) => ({
                valor: String(r.id ?? `recurso-${i}`),
                original: r,
              }))}
              valor={selectedResource?.id != null ? String(selectedResource.id) : undefined}
              onSeleccionar={handleResourceSelect}
              renderContenido={(resource) => (
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 pr-6">
                    <h3 className="truncate font-semibold text-neutral-900">{resource.name}</h3>
                    {/* Mismo caso que la descripción del servicio, dos pasos más
                        arriba: sin texto propio no se inventa uno. */}
                    {resource.description?.trim() && (
                      <p className="mt-0.5 text-sm leading-relaxed text-neutral-600">
                        {resource.description}
                      </p>
                    )}
                  </div>
                </div>
              )}
            />
          )}
          </PasoDeReserva>
        )}

      {/* Booking Section - Only show if all required selections are made */}
        {canShowCalendar() && (
          <PasoDeReserva
            key="paso-dia-y-hora"
            id="booking-section"
            numero={numeroDePaso('fecha')}
            titulo="Elegí el día y la hora"
            // El fondo blanco y el relleno eran `.booking-section`, que además
            // usa BrandPreview. El relleno no puede vivir en la sección —es la
            // que anima su alto— así que se pasa al div de adentro con las
            // mismas medidas: 4rem, y 3rem arriba sin nada abajo en teléfono.
            className="bg-white"
            contenidoClassName="mx-auto max-w-5xl px-2 pt-12 pb-0 sm:px-6 md:py-16"
          >
            <div className="booking-container">
            <BookingCalendar
              key={`${selectedService?.name}-${selectedStaff?.id}-${selectedResource?.id}-${isRescheduling}` || 'no-selection'} // Forzar re-mount cuando cambie cualquier selección
              businessInfo={businessData}
              services={[selectedService]} // Solo pasar el servicio seleccionado
              selectedStaff={selectedStaff}
              selectedResource={selectedResource}
              onSelectSlot={handleSelectSlot}
              refreshTrigger={refreshTrigger}
              isRescheduling={isRescheduling}
              currentBooking={currentBooking} // Pasar turno actual para marcarlo visualmente
              alMostrarHorarios={irAHorarios}
            />
            </div>
          </PasoDeReserva>
        )}

      {/* Empty Service Card */}
      {showEmptyCard && (
        <section className="empty-card-section">
          <div className="container">
            <EmptyServiceCard
              bookingData={bookingData}
              // Datos de paciente: sólo en los consultorios. Ver `pideCobertura`.
              pideDatosDePaciente={pideCobertura}
              coberturas={coberturasDelNegocio}
              // Para buscar al paciente por DNI en la agenda de ESTE negocio.
              // En vista previa no hay slug —el negocio todavía no existe— y la
              // búsqueda simplemente no corre.
              slugDelNegocio={slug}
              onConfirm={handleConfirmBooking}
              onCancel={handleCancelEmptyCard}
              isDemo={isDemo}
            />
          </div>
        </section>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className={`modal-overlay-success${confirmationClosing ? ' closing' : ''}`} onClick={handleCloseConfirmation}>
          <div className={`confirmation-modal${confirmationClosing ? ' closing' : ''}`} onClick={e => e.stopPropagation()}>
            <header className="confirmation-header">
              <div className="confirmation-icon">✅</div>
              <h2 className="confirmation-title">¡Reserva confirmada!</h2>
              <p className="confirmation-message">
                {reservationDetails.customerName}, tu turno en {reservationDetails.businessName} fue agendado exitosamente.
                {/* Solo mostrar mensaje si el usuario eligió "Sin preferencia", no si el servicio es automático */}
                {reservationDetails.wasStaffAutoAssigned && selectedService?.staffSelectionMode === 'optional' && (
                  <><br /><span className="auto-assignment-message">
                    Se te asignó automáticamente un profesional disponible para tu horario.
                  </span></>
                )}
              </p>
            </header>

            {/* Dos columnas en escritorio: los datos del turno a la izquierda y
                el código con el calendario a la derecha. En una sola columna
                esto medía más que la pantalla y había que scrollear para llegar
                a "Entendido", con espacio de sobra a los costados. Abajo de
                768px vuelve a ser una columna, que ahí sí es lo que corresponde. */}
            <div className="confirmation-cuerpo">
              <div className="confirmation-columna">
                <div className="confirmation-details">
                  <div className="confirmation-row">
                    <span className="confirmation-detail-icon">•</span>
                    <span className="confirmation-service">{reservationDetails.service}</span>
                  </div>
                  {/* Solo mostrar el staff si el usuario lo eligió manualmente */}
                  {reservationDetails.staff && !reservationDetails.wasStaffAutoAssigned && (
                    <div className="confirmation-row">
                      <span className="confirmation-detail-icon">👨‍⚕️</span>
                      <span className="confirmation-staff">{reservationDetails.staff.name}</span>
                    </div>
                  )}
                  {reservationDetails.resource && reservationDetails.resource.name && (
                    <div className="confirmation-row">
                      <span className="confirmation-detail-icon">📍</span>
                      <span className="confirmation-resource">{reservationDetails.resource.name}</span>
                    </div>
                  )}
                  <div className="confirmation-row">
                    <span className="confirmation-detail-icon">📅</span>
                    <span className="confirmation-date">{reservationDetails.date}</span>
                  </div>
                  <div className="confirmation-row">
                    <span className="confirmation-detail-icon">🕐</span>
                    <span className="confirmation-time">{reservationDetails.time}</span>
                  </div>
                  <div className="confirmation-row">
                    <span className="confirmation-detail-icon">💰</span>
                    <span className="confirmation-price">{reservationDetails.price}
                      {reservationDetails.priceMultiplier && reservationDetails.priceMultiplier !== 1 && (
                        <span className="confirmation-price-variacion">
                          ({reservationDetails.priceMultiplier > 1 ? `+${Math.round((reservationDetails.priceMultiplier - 1) * 100)}%` : `-${Math.round((1 - reservationDetails.priceMultiplier) * 100)}%`})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="confirmation-columna">
                <div className="confirmation-code-section">
                  <span className="confirmation-code-label">Código de reserva</span>
                  <div className="confirmation-code-fila">
                    <span className="confirmation-code">{reservationDetails.code || 'GENERANDO...'}</span>
                    {reservationDetails.code && (
                      <button
                        type="button"
                        className="btn-copy-code"
                        title="Copiar código"
                        aria-label="Copiar código de reserva"
                        onClick={() => {
                          navigator.clipboard.writeText(reservationDetails.code);
                          toast.success('Código copiado');
                        }}
                      >
                        <Copy size={15} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  <p className="confirmation-note">
                    Te lo enviamos por <strong>email</strong>
                  </p>
                </div>

                {/* Agregar el turno al calendario del cliente. El componente
                    decide qué hace cada destino: Google y Outlook por link a su
                    formulario, Apple por .ics. */}
                {reservationDetails.fechaISO && reservationDetails.hora && (
                  <div className="confirmation-calendar">
                    <span className="confirmation-calendar-label">Agregalo a tu calendario</span>
                    <BotonesDeCalendario
                      evento={{
                        titulo: `${reservationDetails.service} - ${reservationDetails.businessName}`,
                        descripcion: reservationDetails.code
                          ? `Código de reserva: ${reservationDetails.code}`
                          : '',
                        lugar: businessData?.businessInfo?.address || businessData?.address || '',
                        fechaISO: reservationDetails.fechaISO,
                        hora: reservationDetails.hora,
                        duracionMinutos: reservationDetails.duracionMinutos,
                        uid: reservationDetails.code
                          ? `${reservationDetails.code}@aturno.app`
                          : undefined,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="confirmation-actions">
              <button
                type="button"
                onClick={() => setShowWaitingListModal(true)}
                className="confirmation-btn confirmation-btn-secundario"
              >
                Preferir otro horario
              </button>
              <button
                type="button"
                onClick={handleCloseConfirmation}
                className="confirmation-btn confirmation-btn-principal"
              >
                Entendido
              </button>
            </div>

            {/* Solo en la demo: cierra el círculo mostrando el turno recién
                sacado, ya cargado en el panel del negocio. Es el momento en el
                que se entiende de qué se trata el producto. */}
            {isDemo && (
              <button
                onClick={() => navigate('/demo/dashboard')}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-neutral-300 px-6 py-3 text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-50"
              >
                <LayoutDashboard className="h-4 w-4" />
                Ver cómo le llega al negocio
              </button>
            )}
          </div>
        </div>
      )}

      {/* Gestionar un turno: buscar por código, y de ahí reprogramar o cancelar.
          Estaba maquetado con doce clases propias en BusinessPage.css, escritas
          DOS veces en el mismo archivo con valores distintos (radio 20px contra
          16px, padding 3rem contra 2rem, un degradado naranja-violeta contra el
          color de la marca). Ganaba la segunda por estar más abajo, así que la
          primera era código muerto que igual había que leer para entender qué
          se veía.

          Ahora usa lo mismo que el resto de la página pública: el <Button> del
          sistema —que toma el color del negocio solo— y las clases de Tailwind
          con la escala de neutros. Un modal que se pinta con hexadecimales
          propios se ve de otra app cuando el negocio elige su color. */}
      {showManageModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
          onClick={resetManageModal}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl sm:p-8"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-xl leading-none text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
              onClick={resetManageModal}
              aria-label="Cerrar"
            >
              ×
            </button>

            {/* Paso 1: Buscar reserva */}
            {managementStep === "search" && (
              <>
                <h2 className="pr-10 text-xl font-bold tracking-tight text-neutral-900">
                  Gestionar tu turno
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">
                  Poné el código que te llegó por mail para cambiarlo de horario o cancelarlo.
                </p>
                {/* `maxLength` 9, que es lo que mide el código con el guión.
                    Estaba en 6 —el largo de los códigos viejos— y el código pasó
                    a ser de 8 más el guión: el campo cortaba lo que se pegaba y
                    no había NINGUNA forma de gestionar un turno. Que sea el
                    largo del código y no un número suelto es lo que evita que se
                    vuelvan a desincronizar.

                    El `onChange` sólo pasa a mayúscula: normalizar acá el guión
                    y los espacios pelearía con quien está tipeando. De eso se
                    encarga el backend, que compara contra la forma canónica
                    (backend/src/codigos.js). */}
                {/* Monoespaciado y espaciado entre letras: es un código que se
                    copia de un mail carácter por carácter, y con la tipografía
                    del texto no se distingue lo que se tipeó de lo que se quiso
                    tipear. */}
                <input
                  type="text"
                  placeholder="ABCD-2345"
                  value={managementCode}
                  onChange={(e) => setManagementCode(e.target.value.toUpperCase())}
                  className="mt-5 w-full rounded-xl border border-neutral-200 px-4 py-3 text-center font-mono text-lg tracking-[0.2em] text-neutral-900 outline-none transition-colors placeholder:tracking-normal placeholder:text-neutral-400 focus:border-primary"
                  maxLength={9}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                />

                {managementError && (
                  <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                    {managementError}
                  </p>
                )}

                <Button
                  onClick={handleCodeSubmit}
                  className="mt-5 w-full"
                  disabled={isProcessing || !managementCode.trim()}
                >
                  {isProcessing ? "Buscando..." : "Buscar mi turno"}
                </Button>
              </>
            )}

            {/* Paso 2: Opciones de gestión */}
            {managementStep === "options" && foundBooking && (
              <>
                <h2 className="pr-10 text-xl font-bold tracking-tight text-neutral-900">
                  Tu turno
                </h2>

                {/* Los datos como una ficha con filas etiqueta/valor, en vez de
                    párrafos con <strong> adelante: alineados en dos columnas se
                    leen de un vistazo, que es lo único que hay que hacer acá
                    antes de decidir. */}
                <dl className="mt-5 divide-y divide-neutral-100 rounded-xl border border-neutral-200 bg-neutral-50/60 px-4">
                  <div className="py-3">
                    <dt className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                      Servicio
                    </dt>
                    <dd className="mt-0.5 font-semibold text-neutral-900">
                      {foundBooking.service.name}
                    </dd>
                  </div>
                  {/* El profesional y el lugar entran sólo si el turno los
                      tiene: hay servicios que no eligen ninguno de los dos, y
                      una fila "Profesional: —" es ruido.

                      Se leen de varias claves porque una reserva las guarda
                      distinto según por dónde entró: la página pública escribe
                      `staff`/`resource`, el camino con seña deja
                      `assignedStaff`/`assignedResource`, y los recursos
                      específicos guardan el nombre en `resourceName`. Es el
                      mismo abanico que ya contempla el panel. */}
                  {[
                    ['Fecha', formatDateSafely(foundBooking.date)],
                    ['Hora', `${foundBooking.time} hs`],
                    [
                      'Profesional',
                      foundBooking.staff?.name ||
                        foundBooking.assignedStaff?.name ||
                        (typeof foundBooking.staff === 'string' ? foundBooking.staff : null),
                    ],
                    [
                      'Lugar',
                      foundBooking.resource?.name ||
                        foundBooking.assignedResource?.resourceName ||
                        foundBooking.assignedResource?.name ||
                        foundBooking.resourceName ||
                        null,
                    ],
                    ['A nombre de', foundBooking.customer?.name],
                  ]
                    .filter(([, valor]) => valor)
                    .map(([etiqueta, valor]) => (
                      <div key={etiqueta} className="flex items-baseline justify-between gap-4 py-3">
                        <dt className="shrink-0 text-sm text-neutral-500">{etiqueta}</dt>
                        <dd className="text-right text-sm font-medium text-neutral-900">{valor}</dd>
                      </div>
                    ))}
                  <div className="flex items-baseline justify-between gap-4 py-3">
                    <dt className="text-sm text-neutral-500">Código</dt>
                    <dd className="font-mono text-sm font-semibold tracking-wider text-neutral-900">
                      {foundBooking.code}
                    </dd>
                  </div>
                </dl>

                {/* Reprogramar es la acción esperable y va primero, con el peso
                    del botón principal. Cancelar es la irreversible: va como
                    contorno y en rojo, para que no se toque de paso.

                    El botón nombra las tres cosas que se pueden cambiar y no
                    sólo el día: reprogramar vuelve al principio de la reserva,
                    donde se elige de nuevo profesional, lugar y horario. Decir
                    "cambiar día" hace que quien quiere otro profesional no
                    encuentre por dónde. */}
                <div className="mt-5 flex flex-col gap-2">
                  <Button onClick={handleRescheduleBooking} disabled={isProcessing}>
                    Cambiar día, horario o profesional
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancelBooking}
                    disabled={isProcessing}
                    className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                  >
                    {isProcessing ? "Cancelando..." : "Cancelar el turno"}
                  </Button>
                </div>

                <button
                  onClick={() => setManagementStep("search")}
                  className="mt-4 w-full text-sm font-medium text-neutral-500 underline-offset-4 transition-colors hover:text-neutral-900 hover:underline"
                >
                  ← Buscar otro turno
                </button>
              </>
            )}

            {/* Paso 3: Reprogramar eliminado - ahora se hace en la página principal */}

            {managementError && managementStep !== "search" && (
              <p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                {managementError}
              </p>
            )}
          </div>
        </div>
      )}

      {/* 💰 Modal de Pago de Seña */}
      {showDepositModal && pendingBookingData && (
        <DepositPaymentModal
          service={pendingBookingData.service}
          bookingInfo={pendingBookingData}
          onPaymentSelected={handleDepositPaymentSelected}
          onCancel={handleDepositCancel}
          businessData={businessData}
        />
      )}

      {/* 📋 Modal de Lista de Espera */}
      {showWaitingListModal && reservationDetails && (
        <WaitingListModal
          isOpen={showWaitingListModal}
          onClose={() => setShowWaitingListModal(false)}
          businessData={businessData}
          currentBooking={{
            customer: {
              name: reservationDetails.customerName,
              phone: reservationDetails.customerPhone,
              email: reservationDetails.customerEmail || ''
            },
            bookingId: reservationDetails.bookingId || null
          }}
          selectedService={reservationDetails.serviceData || selectedService}
          staff={businessData?.staff || []}
          resources={businessData?.resources || []}
        />
      )}

      {/* 🛡️ Modal de Captcha Anti-Spam */}
      {showCaptcha && pendingBookingForSpam && (
        <AntiSpamCaptcha
          businessId={businessData?.id}
          onVerified={handleCaptchaVerified}
          onCancel={handleCaptchaCancel}
        />
      )}

      {/* Resumen fijo al pie, solo en teléfono. Deja siempre a la vista qué
          eligió el cliente y cuánto sale, en una página que es larga. */}
      <ResumenReserva
        servicio={selectedService}
        profesional={selectedStaff}
        recurso={selectedResource}
      />
      </div>
  );
}


// ===============================================
// FIN: Subpaso 1.2 - Conectar página pública a la API
// =============================================== 